import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight } from 'lucide-react';
import { STATS, SERVICES, WHO_WE_SERVE, IMAGES } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import { useTranslation } from '../translations/translations';

const Home = () => {
  const { language } = useLanguage();
  const t = useTranslation(language);

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{t.meta.home.title}</title>
        <meta name="description" content={t.meta.home.description} />
      </Helmet>
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.hero}
            alt="Financial consulting"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a1628]/95 via-[#0a1628]/90 to-[#0a1628]/80" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
              {t.home.heroTitle}{' '}
              <span className="text-[#2ecc8a]">{t.home.heroHighlight}</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-8 leading-relaxed">
              {t.home.heroSubtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 bg-[#2ecc8a] text-[#0a1628] font-semibold rounded-lg hover:bg-[#26b377] transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
              >
                {t.home.getStarted}
                <ArrowRight className="ml-2" size={20} />
              </Link>
              <Link
                to="/services"
                className="inline-flex items-center justify-center px-8 py-4 bg-white/10 backdrop-blur-sm text-white font-semibold rounded-lg hover:bg-white/20 transition-all duration-300 border border-white/20"
              >
                {t.home.exploreServices}
              </Link>
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
            <div className="w-1.5 h-3 bg-[#2ecc8a] rounded-full" />
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-[#0a1628]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center transform hover:scale-105 transition-transform duration-300">
              <div className="text-4xl md:text-5xl font-bold text-[#2ecc8a] mb-2">
                $1B+
              </div>
              <div className="text-gray-400 font-medium text-sm">
                {language === 'fr' ? 'Budgets et prévisions gérés' : 'Budget and Forecast Managed'}
              </div>
            </div>
            <div className="text-center transform hover:scale-105 transition-transform duration-300">
              <div className="text-4xl md:text-5xl font-bold text-[#2ecc8a] mb-2">
                15+
              </div>
              <div className="text-gray-400 font-medium text-sm">
                {language === 'fr' ? 'Années d\'expérience PE & FP&A' : 'Years of PE & FP&A Experience'}
              </div>
            </div>
            <div className="text-center transform hover:scale-105 transition-transform duration-300">
              <div className="text-4xl md:text-5xl font-bold text-[#2ecc8a] mb-2">
                50+
              </div>
              <div className="text-gray-400 font-medium text-sm">
                {language === 'fr' ? 'Projets livrés' : 'Projects Delivered'}
              </div>
            </div>
            <div className="text-center transform hover:scale-105 transition-transform duration-300">
              <div className="text-4xl md:text-5xl font-bold text-[#2ecc8a] mb-2">
                100%
              </div>
              <div className="text-gray-400 font-medium text-sm">
                {language === 'fr' ? 'Satisfaction client' : 'Client Satisfaction'}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-gray-600 font-medium mb-6">
            {t.home.trustBar}
          </p>
          <div className="flex justify-center items-center space-x-8 text-gray-400">
            <div className="text-2xl font-bold">{t.home.smbs}</div>
            <div className="w-px h-8 bg-gray-300" />
            <div className="text-2xl font-bold">{t.home.peCompanies}</div>
            <div className="w-px h-8 bg-gray-300" />
            <div className="text-2xl font-bold">{t.home.strategicPartners}</div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0a1628] mb-4">
              {t.home.ourServices}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t.home.servicesSubtitle}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {SERVICES.map((service, index) => {
              const serviceKey = index === 0 ? 'fpa' : index === 1 ? 'pmi' : index === 2 ? 'performance' : 'valueCreation';
              return (
                <div
                  key={service.id}
                  className="group relative bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-200 hover:border-[#2ecc8a]"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={service.image}
                      alt={t.services[serviceKey].title}
                      className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0a1628]/80 to-transparent" />
                  </div>
                  <div className="p-8">
                    <h3 className="text-2xl font-bold text-[#0a1628] mb-3 group-hover:text-[#2ecc8a] transition-colors duration-300">
                      {t.services[serviceKey].title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed mb-4">
                      {t.services[serviceKey].short}
                    </p>
                    <Link
                      to="/services"
                      className="inline-flex items-center text-[#2ecc8a] font-semibold hover:gap-2 transition-all duration-300"
                    >
                      {t.home.learnMore}
                      <ArrowRight className="ml-1" size={18} />
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Who We Serve Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0a1628] mb-4">
              {t.home.whoWeServeTitle}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t.home.whoWeServeSubtitle}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 p-8">
              <h3 className="text-2xl font-bold text-[#0a1628] mb-4">
                {t.home.peBackedCard.title}
              </h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                {t.home.peBackedCard.body}
              </p>
              <Link
                to="/who-we-serve"
                className="inline-flex items-center text-[#2ecc8a] font-semibold hover:gap-2 transition-all duration-300"
              >
                {t.home.learnMore}
                <ArrowRight className="ml-1" size={18} />
              </Link>
            </div>

            <div className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 p-8">
              <h3 className="text-2xl font-bold text-[#0a1628] mb-4">
                {t.home.smbCard.title}
              </h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                {t.home.smbCard.body}
              </p>
              <Link
                to="/who-we-serve"
                className="inline-flex items-center text-[#2ecc8a] font-semibold hover:gap-2 transition-all duration-300"
              >
                {t.home.learnMore}
                <ArrowRight className="ml-1" size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* 90-Day Post-Close Problem Section */}
      <section className="py-20 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0a1628] mb-6">
              {t.home.problem90DayTitle}
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed mb-8">
              {t.home.problem90DayText}
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center px-8 py-4 bg-[#0a1628] text-white font-semibold rounded-lg hover:bg-[#0f1f38] transition-all duration-300 shadow-md hover:shadow-lg"
            >
              {t.home.scheduleConsultation}
              <ArrowRight className="ml-2" size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#0a1628] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-[#2ecc8a] rounded-full filter blur-3xl" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#2ecc8a] rounded-full filter blur-3xl" />
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            {t.home.ctaTitle}
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            {t.home.ctaSubtitle}
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center px-8 py-4 bg-[#2ecc8a] text-[#0a1628] font-semibold rounded-lg hover:bg-[#26b377] transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
          >
            {t.home.scheduleConsultation}
            <ArrowRight className="ml-2" size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
